// Tiny typed i18n: per-locale dictionaries (./messages.ts), a pure
// `translate()` and a `useT()` hook bound to the locale in the settings
// store. No runtime dependency — the game only needs a handful of strings.

import { useCallback } from 'react';
import { useSettings } from '../settings/useSettings.js';
import type { Locale } from './locale.js';
import { type MessageKey, type Messages, en, es } from './messages.js';

export {
  LOCALES,
  LOCALE_NAMES,
  LOCALE_FLAGS,
  type Locale,
  browserLocale,
  detectLocale,
  isLocale,
} from './locale.js';
export type { MessageKey, Messages } from './messages.js';

const DICTIONARIES: Record<Locale, Messages> = { en, es };

export type TranslateVars = Readonly<Record<string, string | number>>;
export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

/** Look up `key` in `locale`, falling back to English, and fill `{placeholders}`. */
export function translate(locale: Locale, key: MessageKey, vars?: TranslateVars): string {
  const template = DICTIONARIES[locale]?.[key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Translator bound to the player's chosen locale; re-renders when it changes. */
export function useT(): Translate {
  const locale = useSettings((s) => s.locale);
  return useCallback<Translate>((key, vars) => translate(locale, key, vars), [locale]);
}

/** The active locale (for `<html lang>` and pickers). */
export function useLocale(): Locale {
  return useSettings((s) => s.locale);
}
